// Progressive-disclosure edit form shared by Review and Edit (CONTRACTS §10).
// - Only POPULATED fields are shown by default; a "Show hidden fields" expander
//   reveals the empty ones. Every field is editable.
// - Each field shows its provenance via <SourceBadge>; caption-sourced fields
//   are marked AUTHORITATIVE (CONTRACTS §4).
// - styleTags / generalTags edited via <TagEditor>; tourTags lead the card top
//   in <TourTagSection> (DESIGN §9), so they are NOT rendered here.

import { useState } from "react";
import type { Artifact, FieldSource } from "../types";
import type { Draft, UseArtifactDraft } from "../draft";
import { SourceBadge } from "./SourceBadge";
import { TagEditor } from "./TagEditor";

interface ArtifactFormProps {
  artifact: Artifact;
  d: UseArtifactDraft;
  idPrefix: string;
}

/** A draft field counts as "populated" if either the saved record or the live draft has content. */
function isPopulated(draft: Draft, key: keyof Draft): boolean {
  const v = draft[key];
  return Array.isArray(v) ? v.length > 0 : v.trim() !== "";
}

function sourceOf(artifact: Artifact, field: string): FieldSource | undefined {
  return artifact.fieldSources?.[field];
}

export function ArtifactForm({ artifact, d, idPrefix }: ArtifactFormProps) {
  const [showHidden, setShowHidden] = useState(false);
  const { draft, setText, setNumber, setTags } = d;

  // General items skip the art lookup; their freeform `notes` field is the main
  // descriptive content, so it's surfaced prominently (not hidden) for them.
  const isGeneral = artifact.kind === "general";

  // Field render order (mirrors the contract's field list).
  const textFields: { key: "title" | "artist" | "dateDisplay" | "country" | "medium" | "dimensions"; label: string }[] = [
    { key: "title", label: "Title" },
    { key: "artist", label: "Artist" },
    { key: "dateDisplay", label: "Date (display)" },
    { key: "country", label: "Country / school" },
    { key: "medium", label: "Medium" },
    { key: "dimensions", label: "Dimensions" },
  ];

  const rows: React.ReactNode[] = [];

  const renderRow = (
    key: keyof Draft,
    label: string,
    control: React.ReactNode,
    sourceField = key as string,
    forceShow = false,
    extraClass = "",
  ) => {
    const populated = isPopulated(draft, key);
    if (!populated && !showHidden && !forceShow) return; // hidden until expander toggled
    const src = sourceOf(artifact, sourceField);
    rows.push(
      <div
        className={
          "field-row" +
          (src === "caption" ? " field-row--authoritative" : "") +
          (!populated ? " field-row--hidden-field" : "") +
          (extraClass ? " " + extraClass : "")
        }
        key={key as string}
      >
        <div className="field-row__head">
          <label className="field-row__label" htmlFor={`${idPrefix}-${key as string}`}>
            {label}
          </label>
          <SourceBadge source={src} />
        </div>
        {control}
      </div>,
    );
  };

  const renderNotes = (forceShow: boolean, extraClass: string) =>
    renderRow(
      "notes",
      "Notes",
      <textarea
        id={`${idPrefix}-notes`}
        className="field-input field-textarea"
        rows={isGeneral ? 4 : 2}
        placeholder={isGeneral ? "Describe this item, talking points, etc." : ""}
        value={draft.notes}
        onChange={(e) => setText("notes", e.target.value)}
      />,
      "notes",
      forceShow,
      extraClass,
    );

  // For general items, notes is the primary descriptive field — render it first
  // and always visible (prominent). For artwork it lives with the optional fields
  // below (only shown when populated or "show hidden fields" is toggled).
  if (isGeneral) renderNotes(true, "field-row--notes-prominent");

  // --- text fields ---
  for (const f of textFields) {
    renderRow(
      f.key,
      f.label,
      <input
        id={`${idPrefix}-${f.key}`}
        className="field-input"
        type="text"
        value={draft[f.key]}
        onChange={(e) => setText(f.key, e.target.value)}
      />,
    );
  }

  // --- years (start/end) on one row ---
  {
    const populated = isPopulated(draft, "yearStart") || isPopulated(draft, "yearEnd");
    if (populated || showHidden) {
      // Use whichever source is recorded for the year fields (dateDisplay-driven).
      const src =
        sourceOf(artifact, "yearStart") ??
        sourceOf(artifact, "yearEnd") ??
        sourceOf(artifact, "dateDisplay");
      rows.push(
        <div
          className={
            "field-row" +
            (src === "caption" ? " field-row--authoritative" : "") +
            (!populated ? " field-row--hidden-field" : "")
          }
          key="years"
        >
          <div className="field-row__head">
            <span className="field-row__label">Years (negative = BCE)</span>
            <SourceBadge source={src} />
          </div>
          <div className="field-years">
            <input
              id={`${idPrefix}-yearStart`}
              className="field-input field-input--year"
              type="number"
              inputMode="numeric"
              placeholder="start"
              value={draft.yearStart}
              onChange={(e) => setNumber("yearStart", e.target.value)}
            />
            <span className="field-years__dash">–</span>
            <input
              id={`${idPrefix}-yearEnd`}
              className="field-input field-input--year"
              type="number"
              inputMode="numeric"
              placeholder="end"
              value={draft.yearEnd}
              onChange={(e) => setNumber("yearEnd", e.target.value)}
            />
          </div>
        </div>,
      );
    }
  }

  // --- tag fields (tourTags is hoisted to the card top; see TourTagSection) ---
  const tagFields: { key: "styleTags" | "generalTags"; label: string; slug?: boolean; placeholder: string }[] = [
    { key: "styleTags", label: "Style tags", placeholder: "e.g. Baroque" },
    { key: "generalTags", label: "General tags", placeholder: "e.g. portrait" },
  ];
  for (const f of tagFields) {
    renderRow(
      f.key,
      f.label,
      <TagEditor
        inputId={`${idPrefix}-${f.key}`}
        values={draft[f.key]}
        slug={f.slug}
        placeholder={f.placeholder}
        onChange={(next) => setTags(f.key, next)}
      />,
    );
  }

  // --- misc (free text) ---
  renderRow(
    "misc",
    "Misc",
    <textarea
      id={`${idPrefix}-misc`}
      className="field-input field-textarea"
      rows={2}
      value={draft.misc}
      onChange={(e) => setText("misc", e.target.value)}
    />,
  );

  // --- notes (free text). For artwork it sits with the optional fields here;
  //     general items already rendered it prominently at the top. ---
  if (!isGeneral) renderNotes(false, "");

  // --- captionText: verbatim caption. Always rendered prominently (authoritative source). ---
  renderRow(
    "captionText",
    "Caption text (verbatim)",
    <textarea
      id={`${idPrefix}-captionText`}
      className="field-input field-textarea field-textarea--caption"
      rows={3}
      value={draft.captionText}
      onChange={(e) => setText("captionText", e.target.value)}
    />,
  );

  // Count hidden (empty) fields to label the expander. tourTags lives at the card
  // top now (TourTagSection), so it's not part of this form's hidden count. `notes`
  // is counted for artwork (it's an optional field below) but NOT for general items
  // (where it's always rendered prominently at the top, never hidden).
  const allKeys: (keyof Draft)[] = [
    "title", "artist", "dateDisplay", "country", "medium", "dimensions",
    "yearStart", "yearEnd", "styleTags", "generalTags", "misc", "captionText",
    ...(isGeneral ? [] : (["notes"] as (keyof Draft)[])),
  ];
  const hiddenCount = allKeys.filter((k) => !isPopulated(draft, k)).length;

  return (
    <div className="artifact-form">
      {rows}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="btn btn--ghost show-hidden-toggle"
          onClick={() => setShowHidden((s) => !s)}
        >
          {showHidden
            ? "Hide empty fields"
            : `Show hidden fields (${hiddenCount} empty)`}
        </button>
      )}
    </div>
  );
}
